<script setup>
import { ref, onMounted, onUnmounted, computed, inject } from "vue";
import * as Sentry from "@sentry/vue";
import { getSettings, getDefaultSettings } from "../utils/settings";
import fetchJsonWithTimeouts from "../utils/fetchJsonWithTimeouts.js";
import { requestLocation } from "../utils/locationState.js";
import { DeviceInfo } from "../utils/deviceInfo.js";
import { hasSharedArrayBuffer } from "../utils/browser.js";
import { isLandscapeOrientation, onBrowserOrientationChange } from "../utils/browser.js";
import { loadThirdPartyContentRules, registerRules } from "../utils/thirdPartyContentManager.js";
import { generateUUID } from "../utils/uuid.js";
import { hostname } from "../utils/host.js";

import sgInside1 from "@/assets/secure-graphic-demo-SG-inside-1.png";
import sgInside2 from "@/assets/secure-graphic-demo-SG-inside-2.png";
import sgOutside1 from "@/assets/secure-graphic-demo-SG-outside-1.png";
import sgOutside2 from "@/assets/secure-graphic-demo-SG-outside-2.png";

const props = defineProps({
  deviceInfo: DeviceInfo,
  onInitialized: Function,
  isCameraOpening: Boolean,
  toggleCameraAccessModal: Function,
  hasWasmCrashed: Boolean
});

const emit = defineEmits(["wechatInitializeClick"]);

const isSDKLoading = ref(true);
const isPrefixLoading = ref(true);
const isSettingsLoading = ref(true);
const hasCameraPermission = ref(null);
const percentage = ref(10);
const installId = ref();
const prefix = ref();
const settings = ref();
const isInitializing = ref(false);
const isRedirectingToPhotoAuth = ref(false);
const isWechat = props.deviceInfo.isWechat();
const useWorkers = hasSharedArrayBuffer();
const cameraPermissionRequested = ref(false);

// How long to show the splash message before the camera prompt. Only used for first-time users.
const CAMERA_PRIME_DELAY_MS = 3000;

// Cap on the camera permission lookup. Some browsers never settle that promise, and the launch
// path must not wait on it.
const PERMISSION_QUERY_TIMEOUT_MS = 200;

// Marks a camera grant for this browsing session. iOS Safari has no camera entry in the Permissions
// API, so this flag is the only way to spot a returning user there. The session scope is on purpose.
// iOS Safari drops camera grants when the session ends. A longer lived flag would skip priming right
// before a prompt the user still has to answer.
const CAMERA_GRANTED_KEY = "__camera_granted__";

// Progress-driven copy: the splash primes the user through three bands as loading advances.
const progressBand = computed(() => {
  const p = percentage.value;
  if (p <= 30) return { headline: "splash.headline_orient", stage: "splash.stage_setup" };
  if (p <= 70) return { headline: "splash.headline_prime", stage: "splash.stage_almost" };
  return { headline: "splash.headline_handoff", stage: "splash.stage_opening" };
});

// In WeChat the loader waits for a "Start Scanning" tap. That tap asks for the camera.
// Everyone else advances on their own.
const showProgress = computed(() => !isWechat || cameraPermissionRequested.value);

// Secure-graphic demo. Two frames swap every 500ms to make the graphic shimmer.
const SG_INSIDE = [sgInside1, sgInside2];
const SG_OUTSIDE = [sgOutside1, sgOutside2];
const sgFrame = ref(0);
const sgShowInside = ref(true);

// A brand can pin the demo to the placement its own codes use, so consumers only learn the layout
// that is really on the packaging. Anything unset or unknown shows both placements in turn.
const SG_LAYOUT_MODES = ["inside", "outside", "both", "none"];
const SG_LAYOUT_FALLBACK = "both";
const brandingTheme = inject("brandingTheme", ref({}));

const sgLayoutDemo = computed(() => {
  const raw = brandingTheme.value?.sgLayoutDemo;
  const mode = typeof raw === "string" ? raw.trim().toLowerCase() : raw;
  return SG_LAYOUT_MODES.includes(mode) ? mode : SG_LAYOUT_FALLBACK;
});

const showSgDemo = computed(() => sgLayoutDemo.value !== "none");

// For a single placement the shimmer still runs, it just never switches placement.
const sgFrames = computed(() => {
  if (sgLayoutDemo.value === "inside") return SG_INSIDE;
  if (sgLayoutDemo.value === "outside") return SG_OUTSIDE;
  return sgShowInside.value ? SG_INSIDE : SG_OUTSIDE;
});
let sgTimer = null;
let progressTimer = null;

function initInstallId() {
  const storedId = window.localStorage.getItem("installId");
  installId.value = storedId || generateUUID();
  if (!storedId) {
    window.localStorage.setItem("installId", installId.value);
  }
}

function loadScript(src) {
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.crossOrigin = "anonymous";
    script.src = src;

    script.onload = () => resolve();
    script.onerror = (err) => reject(err);

    document.body.append(script);
  });
}

function markSdkLoaded() {
  isSDKLoading.value = false;

  if (!props.hasWasmCrashed) {
    window.localStorage.setItem("__scanner_wasm_init__", "loaded");
  }
}

async function loadSDK() {
  const wasmSDKFile = useWorkers ? "./wasm_st_sdk.js" : "./wasm_st_sdk_no_thread.js";

  const primaryUrl = new URL(wasmSDKFile.toString(), document.baseURI).href;

  const videoBasePath = import.meta.env.VITE_SDK_BASE_PATH;
  const fallbackUrl = new URL(`${videoBasePath}/${wasmSDKFile}`, document.baseURI).href;

  try {
    await loadScript(primaryUrl);
    markSdkLoaded();
  } catch (primaryErr) {
    console.warn("SDK primary loading error:", primaryErr);
    try {
      console.warn(`Falling back to SDK from: ${fallbackUrl}`);
      await loadScript(fallbackUrl);
      markSdkLoaded();
    } catch (fallbackErr) {
      console.error("SDK fallback loading error:", fallbackErr);
      Sentry.captureException(new Error("SDK fallback loading error", { cause: fallbackErr }));
      // Redirect to photo-auth as final fallback
      redirectToPhotoAuth();
    }
  }
}

async function getCustomPrefixes() {
  const custom_prefix_url = import.meta.env.VITE_CUSTOM_PREFIX_URL;
  const res = await fetchJsonWithTimeouts(custom_prefix_url);
  // TODO: handle format versions
  return res.prefixes;
}

async function getDeviceSettings() {
  if (props.deviceInfo.model) {
    Sentry.setContext("Device Model", {
      device: props.deviceInfo.model
    });

    const params = new URL(window.location.href).searchParams;
    return await getSettings(
        props.deviceInfo.model,
        props.deviceInfo.isIos(),
        params.has("untailored")
    );
  }
  return Promise.reject(new Error("No device model"));
}

function handleDeniedCameraPermission() {
  props.toggleCameraAccessModal();
}

const updateProgress = () => {
  if (progressTimer) {
    return;
  }
  progressTimer = window.setInterval(() => {
    if (percentage.value < 80) {
      percentage.value += 10;
    } else if (percentage.value >= 80 && percentage.value < 95) {
      percentage.value += 5;
    } else {
      percentage.value += 1;
    }
    if (percentage.value >= 100) {
      percentage.value = 99;
      clearInterval(progressTimer);
      progressTimer = null;
    }
  }, 500);
};

async function setupCameraPermissionTriggering() {
  cameraPermissionRequested.value = true;

  const constraints = {
    audio: false,
    video: { facingMode: { exact: "environment" } }
  };

  return navigator.mediaDevices
    .getUserMedia(constraints)
    .then((stream) => {
      stream.getVideoTracks().forEach(function (track) {
        track.stop();
      });
      hasCameraPermission.value = true;
      rememberCameraGrant();
    })
    .catch((err) => {
      hasCameraPermission.value = false;
      // NotAllowedError vs other errors
      switch (err.name) {
        case "NotAllowedError":
          forgetCameraGrant();
          handleDeniedCameraPermission();
          break;
        default:
          redirectToPhotoAuth();
      }

      // Reported, not rethrown: setup() gates on hasCameraPermission instead
      Sentry.captureException(err instanceof Error ? err : new Error(`Camera error: ${err}`, { cause: err }));
    });
}

function hasGrantedCameraThisSession() {
  try {
    return window.sessionStorage.getItem(CAMERA_GRANTED_KEY) === "true";
  } catch {
    // Storage can be blocked. We then prime again, which is the safe direction.
    return false;
  }
}

function rememberCameraGrant() {
  try {
    window.sessionStorage.setItem(CAMERA_GRANTED_KEY, "true");
  } catch {
    // Storage can be blocked. The next load just primes again.
  }
}

// A refused camera means any stored grant is out of date. Clearing it brings priming back, which is
// what a user who revoked access in browser settings needs to see.
function forgetCameraGrant() {
  try {
    window.sessionStorage.removeItem(CAMERA_GRANTED_KEY);
  } catch {
    // Nothing to do when storage is blocked.
  }
}

// Returns "granted", "denied" or "prompt". Falls back to "prompt" when the Permissions API cannot
// answer for the camera. The lookup is raced against a short timer so it never stalls the launch.
async function getCameraPermissionState() {
  try {
    if (navigator.permissions && navigator.permissions.query) {
      const timeout = new Promise((resolve) => setTimeout(() => resolve(null), PERMISSION_QUERY_TIMEOUT_MS));
      const status = await Promise.race([navigator.permissions.query({ name: "camera" }), timeout]);
      if (status) {
        return status.state;
      }
    }
  } catch {
    // iOS Safari and some browsers throw for "camera". Treat it as unknown.
  }
  return "prompt";
}

// Only ask for location once the camera is granted. A denied camera shows the help screen or starts
// the photo auth redirect. A location prompt on top of that would just get in the way.
// setupCameraPermissionTriggering reports camera errors but does not rethrow them. Callers cannot
// wait on a rejection, so they check the permission instead.
function requestLocationAfterCameraGrant() {
  if (hasCameraPermission.value) {
    requestLocation();
  }
}

// Give first-time users a few seconds to read the priming message before the camera prompt opens.
// They then know why the camera is needed, so fewer of them decline. The wait is skipped once the
// camera is granted in this session. Browsers that can report a blocked camera skip it too, because
// no prompt will follow. iOS Safari cannot report that, so a blocked camera still waits there.
async function setupCameraWithPriming() {
  const state = await getCameraPermissionState();
  if (state === "prompt" && !hasGrantedCameraThisSession()) {
    await new Promise((resolve) => setTimeout(resolve, CAMERA_PRIME_DELAY_MS));
  }

  // A failed config fetch can start the photo auth redirect while we wait. Asking now would stack
  // an OS dialog on a page that is leaving, and a blocked camera would stay blocked for the origin.
  if (isRedirectingToPhotoAuth.value) {
    return;
  }

  await setupCameraPermissionTriggering();
  // The location prompt opens while the scanner is still loading. A late fix is still picked up.
  requestLocationAfterCameraGrant();
}

async function setupSDKLoading() {
  const [rules] = await Promise.all([loadThirdPartyContentRules(), loadSDK(), window.wasmReady]);

  // A malformed rules file must degrade to scanning without rules, not block scanning.
  try {
    registerRules(rules);
  } catch (error) {
    console.error("Third-party content rules registration error:", error);
    Sentry.captureException(new Error("Third-party content rules registration failed", { cause: error }));
  }
}

async function setupDeviceSettings() {
  await getDeviceSettings()
    .then((deviceSettings) => {
      settings.value = deviceSettings;
      isSettingsLoading.value = false;
      console.log("settings.value:", settings.value);
      if (!settings.value.device_supported) {
        // TODO: should we display a specific screen?
        //       BTW we should never arrive here if we
        //       went through the lobby as we would have been
        //       redirected to photo-auth there.
        redirectToPhotoAuth();
      }
    })
    .catch((reason) => {
      console.error("Settings error:", reason);
      isSettingsLoading.value = false;
      Sentry.captureException(new Error(`Settings error: ${reason}`, { cause: reason }));
      // getSettings falls back to defaults internally on fetch failures, so this catch
      // only sees a missing device model or another unexpected error. Scanning works
      // with the built-in defaults, so neither case should cost the user the video flow.
      settings.value = getDefaultSettings();
    });
}

async function setupCustomPrefix() {
  await getCustomPrefixes()
    .then((prefixes) => {
      prefix.value = prefixes;
      isPrefixLoading.value = false;
    })
    .catch((err) => {
      console.error("Prefix error:", err);
      Sentry.captureException(new Error(`Prefix error: ${err}`, { cause: err }));
      redirectToPhotoAuth();
    });
}

function redirectToPhotoAuth() {
  // Navigation is not instant: without the flag, setup()'s Promise.all can resolve
  // first and initialize() would start the camera with missing config mid-redirect.
  isRedirectingToPhotoAuth.value = true;
  window.location.href = `${window.location.origin}/${import.meta.env.VITE_PHOTO_AUTH_PREFIX}${window.location.search}`;
}

function initialize() {
  if (isRedirectingToPhotoAuth.value) {
    return;
  }
  isInitializing.value = true;
  console.log("Device:", JSON.stringify(props.deviceInfo));
  if (hasCameraPermission.value && (!isWechat || !props.isCameraOpening)) {
    props.onInitialized({
      installId: installId.value,
      prefix: prefix.value,
      settings: settings.value
    });
  }
}

function setup() {
  initInstallId();

  // For WeChat, we'll handle camera permissions separately
  const setupPromises = [setupSDKLoading(), setupDeviceSettings(), setupCustomPrefix()];

  // Outside WeChat, show the splash message first. Then ask for the camera.
  if (!isWechat) {
    setupPromises.push(setupCameraWithPriming());
  }

  Promise.all(setupPromises)
    .then(() => {
      // A denied or failed camera already showed the modal or started the redirect
      if (!isWechat && hasCameraPermission.value) {
        updateProgress();
        initialize();
      }
    })
    .catch((error) => {
      console.error("Setup error:", error);
      Sentry.captureException(error instanceof Error ? error : new Error(`Setup error: ${error}`, { cause: error }));
      redirectToPhotoAuth();
    });
}

function initializeWechatCamera() {
  // The button can turn tappable in the moment before a photo auth redirect leaves the page. Asking
  // for the camera then would leave a refusal stored against the origin.
  if (isRedirectingToPhotoAuth.value) {
    return;
  }

  emit("wechatInitializeClick");
  const startInitialization = () => {
    if (hasCameraPermission.value && !isSDKLoading.value && !isPrefixLoading.value && !isSettingsLoading.value && !isInitializing.value) {
      initialize();
      updateProgress();
    }
  };

  if (!cameraPermissionRequested.value) {
    setupCameraPermissionTriggering().then(() => {
      requestLocationAfterCameraGrant();
      startInitialization();
    });
  } else {
    requestLocationAfterCameraGrant();
    startInitialization();
  }
}

// If the phone is initially in landscape mode, it would not begin setup
let orientationListener;
onMounted(() => {
  // The demo only runs when the brand wants it. Warm just the frames it will show, so the first
  // shimmer does not appear blank on a slow connection. The setting is resolved before this mounts.
  if (showSgDemo.value) {
    const framesToWarm = sgLayoutDemo.value === "both" ? [...SG_INSIDE, ...SG_OUTSIDE] : sgFrames.value;
    framesToWarm.forEach((src) => {
      const frame = new Image();
      frame.src = src;
    });

    // Swap the frame every 500ms. In "both" mode the placement changes too, after four ticks
    // inside and then four ticks below.
    let tick = 0;
    sgTimer = window.setInterval(() => {
      tick = (tick + 1) % 8;
      sgFrame.value = tick % 2;
      sgShowInside.value = tick < 4;
    }, 500);
  }

  if (!isLandscapeOrientation()) {
    setup();
  } else {
    orientationListener = onBrowserOrientationChange((isLandscape) => {
      if (!isLandscape) {
        setup();
        // remove Orientation Listener
        orientationListener();
      }
    });
  }
});

const disableButton = computed(() => {
  return (
    (!hasCameraPermission.value && cameraPermissionRequested.value) ||
    isRedirectingToPhotoAuth.value ||
    props.isCameraOpening ||
    isSDKLoading.value ||
    isPrefixLoading.value ||
    isSettingsLoading.value ||
    isInitializing.value
  );
});

onUnmounted(() => {
  if (sgTimer) {
    clearInterval(sgTimer);
  }
  if (progressTimer) {
    clearInterval(progressTimer);
  }
  if (orientationListener) {
    // Remove orientation listener
    orientationListener();
  }
});
</script>

<template>
<div class="text_all fixed inset-0 flex flex-col overflow-y-auto bg-[var(--background)] text-[var(--text)]">
  <!-- Safety strip: a trust cue pointing at the real address bar. It is pinned so it stays in
         view if the screen ever has to scroll, which is when the user needs it most. -->
  <div
    class="sticky top-0 z-10 flex items-center gap-2.5 border-b py-[11px] pe-3 ps-3.5"
    :style="{
      background: 'linear-gradient(rgba(var(--secondary-rgb), 0.2), rgba(var(--secondary-rgb), 0.2)), #ffffff',
      borderColor: 'var(--secondary)',
      color: '#222222'
    }"
  >
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--secondary)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="shrink-0" aria-hidden="true">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
    <!-- eslint-disable-next-line vue/no-v-html -->
    <p class="flex-1 text-[15px] leading-snug tracking-wide" v-html="$t('splash.address_bar', { domain: hostname })" />
  </div>

  <!-- Body: the headline plus an animated example. Which placements the example shows follows
       the brand's sgLayoutDemo setting, and the brand can turn it off. -->
  <div class="flex flex-1 flex-col items-center justify-center gap-[18px] px-6" aria-live="polite">
    <Transition name="splash-fade" mode="out-in">
      <!-- eslint-disable-next-line vue/no-v-html -->
      <h1 :key="progressBand.headline" class="m-0 text-center text-[clamp(26px,7.7vw,32px)] leading-tight font-bold tracking-wide text-[var(--text)]" v-html="$t(progressBand.headline)" />
    </Transition>
    <!-- The box keeps a fixed height so the layout holds still while the frames swap. It may shrink
         when the screen is too short for it, which happens on a 320px wide screen in the languages
         with the longest headlines. The image scales down with it instead of spilling over. -->
    <div v-if="showSgDemo" class="flex h-[232px] min-h-0 w-[132px] shrink flex-col items-center" aria-hidden="true">
      <img :src="sgFrames[sgFrame]" class="block max-h-full w-full object-contain object-top" alt="" />
    </div>
  </div>

  <!-- Loader + WeChat gate. The "Powered by" line is the shared <Footer>, fixed at the bottom,
       so we leave bottom clearance for it instead of rendering our own attribution row. -->
  <div class="w-full px-4 pb-8">
    <template v-if="showProgress">
      <div class="mb-1.5 flex justify-between px-2 text-[13px] tracking-wide" :style="{ color: 'var(--text)' }">
        <span>
          {{ $t(progressBand.stage) }}
        </span>
        <span>
          {{ percentage }}%
        </span>
      </div>
      <!-- An opaque white base sits under the bar, so the track and fill composite over white
           instead of over the brand background. The track is the secondary color at the same
           20% tint the safety strip uses, so both trust cues read as one family. -->
      <div class="mx-2 h-1.5 overflow-hidden rounded-sm bg-white">
        <div class="h-full w-full" :style="{ background: 'rgba(var(--secondary-rgb), 0.2)' }">
          <span class="block h-full rounded-sm bg-[var(--secondary)] transition-[width] duration-200 ease-linear" :style="{ width: percentage + '%' }" />
        </div>
      </div>
    </template>

    <button
      v-if="isWechat && !cameraPermissionRequested"
      class="mx-auto mt-1 block h-[42px] min-w-[150px] rounded-lg px-6 text-center text-[15px] font-semibold tracking-wide shadow-[0_2px_6px_rgba(0,0,0,0.22)]"
      :class="[disableButton ? 'bg-[#B8B8B8] text-white' : 'bg-[var(--primary)] text-[var(--text-button)]']"
      :disabled="disableButton"
      @click.prevent
      @click="initializeWechatCamera()"
    >
      {{ $t("scan.start_scan") }}
    </button>
  </div>
</div>
</template>

<style scoped>
.splash-fade-enter-active,
.splash-fade-leave-active {
  transition: opacity 0.18s ease;
}
.splash-fade-enter-from,
.splash-fade-leave-to {
  opacity: 0;
}
</style>
